import {
  EOrganization,
  ERate,
  ESnapshot,
  ESnapshotExists,
  EUser,
  exists
} from 'lib/types';
import React from 'react';
import { CardGridLayout } from 'lib/components/Card/Grid';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import { OccupationType, Product } from 'lib/enums';
import { CustomerObjDataFields } from './CreateOrEditModalDetails';
import {
  BillingTermSettings,
  ColumnOnlySettings,
  CustomerDiscountSettings
} from './shared';
import { CustomerAffidavitSettings } from './shared/CustomerAffidavitSettings';
import { ProductRatesSettings } from './shared/ProductRatesSettings';
import { CustomerNoticeRatesSection } from './shared/CustomerNoticeRatesSection';

type AllowAllOccupations = { type: 'all' };
type OccupationWhiteList = { type: 'whitelist'; occupations: number[] };
type OccupationBlackList = { type: 'blacklist'; occupations: number[] };
type OccupationFilter =
  | AllowAllOccupations
  | OccupationWhiteList
  | OccupationBlackList;

type RenderIfRelevantToCustomerProps = {
  occupationFilter: OccupationFilter;
  advertiser: EUser | null;
  children: React.ReactNode;
};

function RenderIfRelevantToCustomer({
  occupationFilter,
  advertiser,
  children
}: RenderIfRelevantToCustomerProps) {
  if (occupationFilter.type === 'all') {
    return <>{children}</>;
  }

  const { occupations } = occupationFilter;
  const { occupation = 0 } = advertiser || {};
  const hasRelevantOccupation = occupations.includes(occupation);

  if (occupationFilter.type === 'whitelist') {
    if (!hasRelevantOccupation) {
      return null;
    }
  }

  if (occupationFilter.type === 'blacklist') {
    if (hasRelevantOccupation) {
      return null;
    }
  }

  return <>{children}</>;
}

export type CreateOrEditModalSettingsProps = {
  activeOrganization: ESnapshot<EOrganization> | null;
  rates?: ESnapshotExists<ERate>[];
  value: CustomerObjDataFields;
  advertiser: EUser | null;
  onChange: (value: CustomerObjDataFields) => void;
};

export function CreateOrEditCustomerModalSettings({
  activeOrganization,
  rates,
  value,
  advertiser,
  onChange
}: CreateOrEditModalSettingsProps) {
  const renderForAll: AllowAllOccupations = { type: 'all' };
  const renderIfNotFuneralDirector: OccupationBlackList = {
    type: 'blacklist',
    occupations: [OccupationType.funeral_director.value]
  };
  const renderIfFuneralDirector: OccupationWhiteList = {
    type: 'whitelist',
    occupations: [OccupationType.funeral_director.value]
  };

  if (!activeOrganization || !exists(activeOrganization)) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <RenderIfRelevantToCustomer
        occupationFilter={renderForAll}
        advertiser={advertiser}
      >
        <CardGridLayout
          header={{
            title: "Set up your customer's preferences",
            description:
              'Configure how you want this customer to interact with your publication.'
          }}
        >
          <BillingTermSettings
            activeOrganization={activeOrganization}
            onChange={(billingTerm: number) =>
              onChange({ ...value, billingTerm })
            }
            value={value.billingTerm}
          />
        </CardGridLayout>
      </RenderIfRelevantToCustomer>

      <RenderIfRelevantToCustomer
        occupationFilter={renderIfNotFuneralDirector}
        advertiser={advertiser}
      >
        <CardGridLayout
          header={{
            title: 'Public Notice Settings',
            description:
              'Configure settings for public notices for this customer.'
          }}
        >
          <ColumnOnlySettings
            activeOrganization={activeOrganization}
            onChange={onChange}
            value={value}
          />
          <CustomerNoticeRatesSection
            value={value}
            onChange={onChange}
            rates={rates || []}
            activeOrganization={activeOrganization}
          />
          <CustomerAffidavitSettings
            activeOrganization={activeOrganization}
            onChange={onChange}
            value={value}
          />
          <CustomerDiscountSettings
            onChange={discountConfig => onChange({ ...value, discountConfig })}
            value={value.discountConfig ?? {}}
          />
        </CardGridLayout>
      </RenderIfRelevantToCustomer>

      <RenderIfRelevantToCustomer
        occupationFilter={renderIfNotFuneralDirector}
        advertiser={advertiser}
      >
        <CardGridLayout
          header={{
            title: 'Classifieds Settings',
            description: 'Configure settings for classifieds for this customer.'
          }}
        >
          <ProductRatesSettings
            productType={Product.Classified}
            value={value}
            onChange={onChange}
            activeOrganization={activeOrganization}
          />
        </CardGridLayout>
      </RenderIfRelevantToCustomer>

      <RenderIfRelevantToCustomer
        occupationFilter={renderIfFuneralDirector}
        advertiser={advertiser}
      >
        <CardGridLayout
          header={{
            title: 'Obituaries Settings',
            description: 'Configure settings for obituaries for this customer.'
          }}
        >
          <ProductRatesSettings
            productType={Product.Obituary}
            value={value}
            onChange={onChange}
            activeOrganization={activeOrganization}
          />
        </CardGridLayout>
      </RenderIfRelevantToCustomer>
    </>
  );
}
